import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as DateCalendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, Building2, Home, Video, MapPin } from 'lucide-react';
import { useAvailableSlots } from '@/services/scheduling/use-available-slots';
import { useOrgLocations } from '@/scheduling/services/use-org-locations';
import { NO_SLOT_AVAILABILITY_MESSAGE } from '@/scheduling/lib/appointment-strings';
import { usePreBookSelectedSlot } from '@/services/scheduling/use-prebook-selected-slot';
import {
  pickDefaultReminder,
  pickAttachedBillingProfile,
  reminderRules,
  formatBillingProfile,
} from '@/services/scheduling/use-prebook-appointment-profile';
import { useAuthStore } from '@/auth/auth-store';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
// PROD-2455 — the time-selection furniture below is now SHARED with the
// Book-a-device rail rather than private to this view. Nothing here changed
// behaviour; it moved so there is one implementation of "what a bookable start
// looks like, how it is grouped, and how an operator types one by hand".
import { CustomTimePopover } from '@/scheduling/components/shared/custom-time-popover';
import { SlotGroupList } from '@/scheduling/components/shared/slot-group-list';
import { formatSlotTime, toSlotItems, type SlotItem } from '@/scheduling/lib/slot-selection';
import type { AppointmentFormValues } from '@/types/appointment-form';
import { formatSlotDate } from './slot-date';

interface TimeDateSelectionViewProps {
  /** Closes the view without changes. */
  onCancel: () => void;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function getStripDays(anchor: Date) {
  return Array.from({ length: 5 }, (_, i) => {
    const d = startOfDay(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });
}

function isSameDay(a: Date, b: Date) {
  return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
}

function getInitials(name: string) {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

function locationIcon(name: string) {
  switch (name) {
    case 'Office':
      return Building2;
    case 'Home':
      return Home;
    case 'Virtual':
      return Video;
    default:
      return MapPin;
  }
}

/**
 * TimeDateSelectionView — full panel-content replacement.
 * Mirrors Flutter `time_date_selection_widget.dart`. Pushed when the user
 * taps the forward arrow on a provider row in `ProviderSelectionView`.
 *
 * Sections:
 *   - PROVIDER (read-only)
 *   - SELECTED SLOT (current pick + "Custom" header link → `CustomTimePopover`)
 *   - SELECT LOCATION (chip row — pre-selected location stays leftmost)
 *   - Date picker (tappable header + 5-day strip)
 *   - Time slots grouped MORNING (00–12) / AFTERNOON (12–17) / EVENING (17–24)
 *
 * Save button is enabled only when a slot is picked AND it differs from
 * the appointment's current `start` (Flutter line 247).
 */
export function TimeDateSelectionView({ onCancel }: TimeDateSelectionViewProps) {
  const { watch, setValue, getValues } = useFormContext<AppointmentFormValues>();
  const providerName = watch('providerName');
  const providerSpeciality = watch('providerSpeciality');
  const selectedDate = watch('selectedDate');
  const appointmentProfileId = watch('appointmentProfileId');
  const minutesDuration = watch('minutesDuration');
  const selectedLocation = watch('location');
  const previousStart = watch('slotStart');

  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';
  const userPractitioner = getValues('providerId');
  const provider = useMemo(
    () => ({ id: userPractitioner ?? '', name: providerName ?? '' }),
    [userPractitioner, providerName],
  );

  // Local state for in-flight picks (not yet committed to form state).
  const today = startOfDay(new Date());
  // Anchor the view on `selectedDate` — the BROWSING date, which the calendar
  // keeps in sync while the panel is open (Flutter `calendar_screen.dart`
  // :2481-2484 `ChangeDateState` → `selectedAppointment.selectedDate`, read
  // here by `time_date_selection_widget.dart:91`). Deliberately NOT
  // `slotStart`: the slot the appointment currently holds is shown in the
  // SELECTED SLOT card, but the day the user is browsing is the calendar's.
  const initialDate = startOfDay(new Date(selectedDate ?? today));
  const [pickerDate, setPickerDate] = useState<Date>(initialDate);
  // Anchor at today so the strip layout matches the panel's date picker;
  // the effect below shifts the anchor only if the pick falls outside the
  // current 5-day window.
  const [anchorDate, setAnchorDate] = useState<Date>(today);
  // Seed the SELECTED SLOT card from the slot the form already holds, so the
  // view opens showing the current time instead of "— : —". Without this the
  // card read empty on every open even though a slot WAS selected, and Save
  // — which requires a pick that DIFFERS from `previousStart` — looked broken.
  const [selectedSlot, setSelectedSlot] = useState<SlotItem | null>(() => {
    const start = getValues('slotStart');
    const end = getValues('slotEnd');
    if (!start || !end) return null;
    return { id: getValues('slotId') ?? `seed-${start}`, start, end };
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  // Keep the local pickerDate aligned with the form's selectedDate so a date
  // change made elsewhere WHILE this view is open is reflected in the slot
  // query and the strip highlight.
  useEffect(() => {
    if (!selectedDate) return;
    const fd = startOfDay(new Date(selectedDate));
    setPickerDate((prev) => (isSameDay(prev, fd) ? prev : fd));
  }, [selectedDate]);

  useEffect(() => {
    setAnchorDate((prev) => {
      const prevEnd = new Date(prev);
      prevEnd.setDate(prev.getDate() + 4);
      const startsBefore = pickerDate.getTime() < prev.getTime();
      const endsAfter = pickerDate.getTime() > prevEnd.getTime();
      return startsBefore || endsAfter ? pickerDate : prev;
    });
  }, [pickerDate]);

  // Org locations for the chip row. Keep the selected one leftmost
  // (Flutter parity: `time_date_selection_widget.dart:158–166`).
  const { data: locations = [] } = useOrgLocations(orgId);
  const orderedLocations = useMemo(() => {
    if (!selectedLocation || locations.length === 0) return locations;
    const idx = locations.findIndex((l) => l.id === selectedLocation.id);
    if (idx <= 0) return locations;
    const copy = [...locations];
    const [picked] = copy.splice(idx, 1);
    copy.unshift(picked);
    return copy;
  }, [locations, selectedLocation]);

  // Today is the floor — past dates aren't bookable. Flutter ships
  // `firstDate = DateTime.now().subtract(Duration(days: 1))` as a
  // buffer, but we tighten to today (matches user requirement).
  const minDate = startOfDay(new Date());

  const stripDays = getStripDays(anchorDate);

  // Slot window ships LOCAL-CLOCK, no `Z` (PROD-1778 rule, see
  // shared/lib/date-format.ts). `toISOString()` converts to UTC first, so in
  // Dubai (GMT+4) local midnight became the PREVIOUS day at 20:00 and the
  // backend — which reads the string literally — answered with the wrong
  // window: measured 2026-08-24 asking as `2026-08-23T20:00:00.000Z` returned
  // Aug 23 20:00 → Aug 24 19:30. That put four PREVIOUS-day chips at the top of
  // the list and hid the tail of the selected day, so a chip could belong to a
  // different day than the strip claimed.
  const { data: apiSlots = [], isLoading } = useAvailableSlots(
    provider.id && practiceId && appointmentProfileId
      ? {
          practiceId,
          practitionerId: provider.id,
          appointmentProfileId,
          start: formatLocalDatetimeForWire(pickerDate),
          end: formatLocalDatetimeForWire(
            new Date(new Date(pickerDate).setHours(23, 59, 59, 0)),
          ),
          count: 100,
        }
      : null,
  );

  // Keys the slots for React and drops any start that has already passed.
  // Both rules now live in `toSlotItems` (PROD-2455) — see its doc comment for
  // the zero-GUID collision and the `>=` boundary this rail has always used.
  const slots: SlotItem[] = toSlotItems(apiSlots, new Date());

  const shiftStrip = (deltaDays: number) => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + deltaDays);
    if (d.getTime() < minDate.getTime()) return;
    setAnchorDate(d);
  };

  const saveEnabled = !!selectedSlot && selectedSlot.start !== previousStart;

  // PreBook the slot on save — refreshes reminder / billing / RFV for
  // the new context (Flutter `calendar_screen.dart:963–971`).
  const preBookSlot = usePreBookSelectedSlot();

  const onSave = () => {
    if (!selectedSlot) return;
    setValue('slotId', selectedSlot.id, { shouldValidate: true });
    setValue('slotStart', selectedSlot.start);
    setValue('slotEnd', selectedSlot.end);
    setValue('selectedDate', selectedSlot.start);

    if (provider.id && appointmentProfileId) {
      // `mutateAsync().then()` — NOT `mutate(vars, { onSuccess })`.
      // `onCancel()` below unmounts this view synchronously, and React Query
      // v5 DROPS a mutation's per-call callbacks when the calling component
      // unmounts before the mutation settles. With `mutate` the reminder /
      // billing refresh silently never ran after a slot change, and a null
      // `reminderProfileId` keeps Save disabled (appointment-form.ts
      // superRefine). Same lesson as appointment-profile-section.tsx:296-306.
      preBookSlot
        .mutateAsync({
          organizationId: orgId,
          practiceId,
          providerId: provider.id,
          appointmentProfileId,
        })
        .then((response) => {
          const reminder = pickDefaultReminder(response.reminderList);
          if (reminder?.id) {
            setValue('reminderProfileId', reminder.id, { shouldValidate: true });
            setValue('reminderProfileName', reminder.profileName ?? null);
            setValue('reminderProfileRules', reminderRules(reminder));
          }
          // Re-fire on slot pick mirrors Flutter
          // (create_appointment_widget.dart:2240/2258) — keeps the
          // billing profile that's attached to the appointment profile.
          // `appointmentProfilesList` isn't on the typed AppointmentFormValues
          // (the field is populated by a side-effect on profile pick but
          // never declared on the schema). Cast the watch invocation.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const appointmentProfilesList = (watch as any)('appointmentProfilesList') as
            | Array<{ id?: string | null; billingProfileId?: string }>
            | undefined;
          const profileForBilling = appointmentProfilesList?.find(
            (p) => p.id === appointmentProfileId,
          );
          // pickAttachedBillingProfile reads `billingProfileId` off the
          // first arg; the appointment-profile row carries that field
          // but its full shape doesn't line up structurally with the
          // helper's minimal signature.
          const billing = pickAttachedBillingProfile(
            profileForBilling as { billingProfileId?: string } | undefined,
            response.billingProfiles,
          );
          if (billing?.id) {
            setValue('billingProfileId', billing.id);
            setValue('billingProfileName', formatBillingProfile(billing));
          }
        })
        .catch(() => {
          // Global MutationCache.onError toast surfaces the failure; the
          // picked slot is already committed to the form.
        });
    }

    onCancel();
  };

  // Compare by START, not id: the seeded slot carries the appointment's own
  // slot id, which never equals a chip's synthetic `${start}-${i}` id —
  // comparing ids left the appointment's current time unhighlighted. It also
  // keeps a CUSTOM time highlighted when it happens to coincide with a chip.
  const isSelected = (s: SlotItem) => selectedSlot?.start === s.start;

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="text-base font-bold text-text-primary">Time &amp; Date</h2>
        <button
          type="button"
          onClick={onSave}
          disabled={!saveEnabled}
          className={cn(
            'text-sm font-medium',
            saveEnabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-6">
          {/* PROVIDER (read-only) */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Provider
            </p>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={undefined} />
                <AvatarFallback className="text-size-xs font-semibold bg-blue-100 text-brand-depth">
                  {getInitials(provider.name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  {provider.name.replace(/^Dr\.?\s*/i, 'Dr. ')}
                </p>
                {providerSpeciality ? (
                  <p className="text-xs text-text-muted truncate">{providerSpeciality}</p>
                ) : null}
              </div>
            </div>
          </div>

          {/* SELECTED SLOT */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-text-muted">
                Selected slot
              </p>
              <CustomTimePopover
                pickerDate={pickerDate}
                durationMinutes={minutesDuration ?? 30}
                initial={selectedSlot?.start ?? null}
                onConfirm={(slot) => setSelectedSlot(slot)}
              />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-border-default bg-surface-card">
              <Calendar className="h-5 w-5 text-brand-primary flex-shrink-0" />
              <div className="text-sm text-text-primary">
                {selectedSlot
                  ? `${formatSlotDate(selectedSlot.start)} · ${formatSlotTime(selectedSlot.start)}`
                  : '— : —'}
              </div>
            </div>
          </div>

          {/* SELECT LOCATION — share row width with flex-1 when <=3 so the
              default Office/Virtual/Home set fits without a horizontal scroll;
              fall back to fixed-width + scroll for 4+ locations. */}
          <div>
            <p className="text-xs font-medium text-text-muted mb-2">
              Select location
            </p>
            <div
              className={cn(
                'flex gap-2',
                orderedLocations.length <= 3 ? '' : 'overflow-x-auto scrollbar-hide pb-1',
              )}
            >
              {orderedLocations.map((loc) => {
                const Icon = locationIcon(loc.name);
                const isSelected = selectedLocation?.id === loc.id;
                const fitsWithoutScroll = orderedLocations.length <= 3;
                return (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() =>
                      setValue('location', { id: loc.id, name: loc.name }, { shouldValidate: true })
                    }
                    className={cn(
                      'flex items-center gap-2 h-[56px] px-3 rounded-lg border transition-colors',
                      fitsWithoutScroll
                        ? 'flex-1 min-w-0'
                        : 'flex-shrink-0 min-w-[120px]',
                      isSelected
                        ? 'bg-blue-100 border-brand-primary text-brand-primary'
                        : 'bg-surface-card border-border-default text-text-muted hover:border-border-strong',
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-semibold truncate">{loc.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={() => shiftStrip(-5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Previous days">
                <ChevronLeft className="h-4 w-4 text-text-muted" />
              </button>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger
                  render={
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-sm font-semibold text-text-primary hover:text-brand-primary"
                    >
                      <span>
                        {MONTH_LONG[pickerDate.getMonth()]} {String(pickerDate.getDate()).padStart(2, '0')},{' '}
                        {pickerDate.getFullYear()}
                      </span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  }
                />
                <PopoverContent align="center" className="w-auto p-2">
                  <DateCalendar
                    mode="single"
                    selected={pickerDate}
                    defaultMonth={pickerDate}
                    onSelect={(d) => {
                      if (d) {
                        setPickerDate(startOfDay(d));
                        setSelectedSlot(null);
                        setPickerOpen(false);
                      }
                    }}
                    disabled={{ before: minDate }}
                    showOutsideDays
                    fixedWeeks
                  />
                  {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value. */}
                  <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
                    <button
                      type="button"
                      onClick={() => {
                        setPickerDate(startOfDay(new Date()));
                        setSelectedSlot(null);
                        setPickerOpen(false);
                      }}
                      className="font-medium text-brand-primary hover:underline"
                    >
                      Today
                    </button>
                    <span className="text-text-muted">
                      {MONTH_LONG[pickerDate.getMonth()]} {String(pickerDate.getDate()).padStart(2, '0')}, {pickerDate.getFullYear()}
                    </span>
                  </div>
                </PopoverContent>
              </Popover>
              <button type="button" onClick={() => shiftStrip(5)} className="p-1 hover:bg-grey-100 rounded" aria-label="Next days">
                <ChevronRight className="h-4 w-4 text-text-muted" />
              </button>
            </div>
            <div className="grid grid-cols-5 gap-1">
              {stripDays.map((d) => {
                const isSelected = isSameDay(d, pickerDate);
                const isPast = d.getTime() < minDate.getTime();
                return (
                  <button
                    key={d.toISOString()}
                    type="button"
                    disabled={isPast}
                    onClick={() => {
                      setPickerDate(d);
                      setSelectedSlot(null);
                    }}
                    className={cn(
                      'flex flex-col items-center py-2 rounded-xl border transition-colors',
                      isSelected
                        ? 'bg-brand-primary border-brand-primary text-white'
                        : isPast
                          ? 'border-border-default text-text-hint cursor-not-allowed opacity-50'
                          : 'border-border-default text-text-muted hover:border-brand-primary hover:text-text-primary',
                    )}
                  >
                    <span className="text-xs font-medium">{MONTH_SHORT[d.getMonth()]}</span>
                    <span className="text-md font-bold leading-tight">{d.getDate()}</span>
                    <span className="text-xs font-medium">{DAY_NAMES[d.getDay()]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time slots */}
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-16 rounded-lg bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : slots.length === 0 ? (
            <p className="text-sm text-text-muted py-2 leading-snug">
              {NO_SLOT_AVAILABILITY_MESSAGE}
            </p>
          ) : (
            <SlotGroupList
              slots={slots}
              name="appointment-slot"
              legend="Time slots"
              isSelected={isSelected}
              onPick={setSelectedSlot}
            />
          )}
        </div>
      </div>

    </div>
  );
}
